import React, { useState } from "react";
import { Mail, Phone, MapPin, Send } from "lucide-react";
import { toast } from "sonner";
import { useSite } from "../lib/SiteContext";
import { submitInquiry } from "../lib/api";

const ContactSection = () => {
  const { content } = useSite();
  const c = content?.contact || {};
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitInquiry(form);
      toast.success("Thanks! We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Could not send. Please try WhatsApp or email.");
    } finally {
      setSubmitting(false);
    }
  };

  const wa = (c.whatsapp || "").replace(/[^0-9]/g, "");

  return (
    <section id="contact" className="section-padding bg-primary text-primary-foreground" data-testid="contact-section">
      <div className="container-x">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="overline mb-4 text-sand">{c.overline}</div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] mb-8 text-balance">
              {c.title}
            </h2>

            <div className="space-y-6 mt-12">
              <a
                href={`mailto:${c.email}`}
                className="flex items-start gap-4 group"
                data-testid="contact-email-link"
              >
                <Mail size={18} className="text-sand mt-1" />
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-white/60">Email</div>
                  <div className="text-lg group-hover:text-sand transition-colors">{c.email}</div>
                </div>
              </a>
              <a
                href={`https://wa.me/${wa}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-4 group"
                data-testid="contact-whatsapp-link"
              >
                <Phone size={18} className="text-sand mt-1" />
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-white/60">WhatsApp</div>
                  <div className="text-lg group-hover:text-sand transition-colors">{c.phone}</div>
                </div>
              </a>
              <div className="flex items-start gap-4">
                <MapPin size={18} className="text-sand mt-1" />
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-white/60">Departure</div>
                  <div className="text-lg">{c.address}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form onSubmit={onSubmit} className="bg-white p-8 md:p-10 text-foreground" data-testid="contact-form">
              <h3 className="font-serif text-2xl md:text-3xl text-primary mb-6">Send a message</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                <input
                  className="input-field"
                  placeholder="Your name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="contact-name"
                />
                <input
                  type="email"
                  className="input-field"
                  placeholder="Email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="contact-email"
                />
              </div>
              <input
                className="input-field mt-4"
                placeholder="Phone (optional)"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="contact-phone"
              />
              <textarea
                className="input-field mt-4 min-h-[140px] resize-none"
                placeholder="Tell us about your dates, group size, and what you'd like to do…"
                required
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                data-testid="contact-message"
              />
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary mt-6 w-full sm:w-auto"
                data-testid="contact-submit"
              >
                {submitting ? "Sending…" : "Send enquiry"} <Send size={14} />
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
